import { icons, HelpCircle, type LucideProps } from "lucide-react";
import React from "react";

type LucideIcon = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

/**
 * All Lucide icons keyed by PascalCase name.
 *
 * Uses the `icons` named export from lucide-react which is a pre-built
 * Record<string, LucideIcon> — immune to tree-shaking (unlike `import *`).
 */
const iconMap = icons as Record<string, LucideIcon>;

/**
 * Returns a Lucide icon component by its PascalCase export name.
 * Always returns a valid component — HelpCircle as fallback.
 *
 * DB must store exact PascalCase names: Tent, Camera, Wrench, PartyPopper, etc.
 *
 * Usage:
 *   const Icon = getLucideIcon(category.icon);
 *   <Icon className="w-5 h-5 text-muted-foreground" />
 */
export function getLucideIcon(iconName?: string | null): LucideIcon {
  if (!iconName || !iconName.trim()) return HelpCircle;

  const name = iconName.trim();
  const Icon = iconMap[name];

  if (Icon) return Icon;

  return HelpCircle;
}

/**
 * Check if a given name resolves to a real Lucide icon (not the fallback).
 * Useful for showing validation state in admin forms.
 */
export function isValidLucideIcon(iconName?: string | null): boolean {
  if (!iconName || !iconName.trim()) return false;
  return iconName.trim() in iconMap;
}

/**
 * Get all available icon names (for icon picker / autocomplete).
 * Returns PascalCase names sorted alphabetically.
 */
export function getAllIconNames(): string[] {
  return Object.keys(iconMap).sort();
}
